<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	title: 'Endorsements',
	endorsements: [
		{
			id: 1,
			body: 'A thoughtful endorsement of this publication.',
			name: 'Prof. Example Scholar',
		},
	],
}
const schema = defineSchema({
	title: $.text('Heading'),
	endorsements: $.array(
		'Items',
		defineSchema({
			id: $.text('ID'),
			body: $.richtext('Quote', { basic: true }),
			name: $.text('Attribution'),
		}),
	{ collapsible: true },),
})
export const BlockTestimonials = defineBlock({ pickerHidden: true,
	thumbnail: '/block-thumbs/BlockTestimonials.svg',
	category: 'Content',
	order: 370,
	name: 'BlockTestimonials',
	label: 'Testimonials',
	icon: 'format_quote',
	schema,
	defaults,
})
</script>
<script setup lang="ts">
import { ref, computed } from 'vue'

import { Btn, Swiper } from '@bagelink/vue'
import { useBoundPageItem } from '@/composables/useBoundPageItem'

/**
 * Generic testimonials / endorsements carousel: a configurable heading over a
 * list of quote + attribution items. Single item renders statically; multiple
 * rotate in a Swiper with prev/next controls.
 *
 * Prefers the bound datastore item's `endorsements`/`testimonials`, else props.
 */




interface Endorsement {
	id: number | string
	body: string
	name: string
}

const props = defineProps<{
	title?: string
	endorsements?: Endorsement[]
}>()

const bound = useBoundPageItem<Record<string, any>>()

const heading = computed(() => props.title || 'Endorsements')
const items = computed<Endorsement[]>(
	() => bound.value?.endorsements ?? bound.value?.testimonials ?? props.endorsements ?? [],
)

const currentSlide = ref(0)

function onSlideChange(index: number) {
	currentSlide.value = index
}
function nextSlide() {
	currentSlide.value = currentSlide.value < items.value.length - 1 ? currentSlide.value + 1 : 0
}
function prevSlide() {
	currentSlide.value = currentSlide.value > 0 ? currentSlide.value - 1 : items.value.length - 1
}
</script>

<template>
	<div v-if="items.length" class="w760px mx-auto pt-3 px-1 relative">
		<h3>{{ heading }}</h3>
		<div class="relative py-2 px-2 m_px-1 bg-gray-90">
			<img src="../../assets/marks.svg" alt="" class="mb-1 h-30px" aria-hidden="true" />
			<div v-if="items.length === 1">
				<div class="txt17 m_txt15" v-html="items[0].body" />
				<p class="bold txt17 m_txt15 pt-1">
					{{ items[0].name }}
				</p>
			</div>
			<div v-else class="">
				<Swiper
					v-model:index="currentSlide"
					:items="items"
					:slides-per-view="1"
					:navigation="false"
					loop
					@slide-change="onSlideChange"
				>
					<template #default="{ item }">
						<div class="pb-4">
							<div class="txt17 m_txt15" v-html="item.body" />
							<p class="bold txt17 m_txt15 pt-1">
								{{ item.name }}
							</p>
						</div>
					</template>
				</Swiper>
				<div class="absolute end-2 -bottom-1 flex gap-05">
					<Btn class="radius-05 bg-gray-120" size="xl" icon="arrow_back" @click="prevSlide" />
					<Btn class="radius-05 bg-gray-120" size="xl" icon="arrow_forward" @click="nextSlide" />
				</div>
			</div>
		</div>
	</div>
</template>
